import { create } from 'zustand';
import {
  archiveRoute,
  assignTruckToRoute,
  createRoute,
  getRoute,
  listRoutes,
  unassignTruckFromRoute,
  updateRoute,
} from '../services/route.service';
import type {
  Route,
  RouteInput,
  RoutePagination,
  RouteQuery,
  RouteUpdateInput,
} from '../types/route.type';

interface RouteState {
  routes: Route[];
  selectedRoute: Route | null;
  total: number;
  page: number;
  limit: number;
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
  selectedRouteNotFound: boolean;
  clearError: () => void;
  fetchRoutes: (query: RouteQuery) => Promise<RoutePagination | null>;
  fetchRoute: (organizationId: string, routeId: string) => Promise<Route | null>;
  createRoute: (organizationId: string, input: RouteInput) => Promise<Route | null>;
  updateRoute: (organizationId: string, routeId: string, input: RouteUpdateInput) => Promise<Route | null>;
  archiveRoute: (organizationId: string, routeId: string) => Promise<boolean>;
  assignTruck: (organizationId: string, routeId: string, truckId: string) => Promise<Route | null>;
  unassignTruck: (organizationId: string, routeId: string) => Promise<Route | null>;
}

function errorMessage(error: unknown, fallback: string): string {
  const candidate = error as { response?: { data?: { detail?: string | { message?: string } } } };
  const detail = candidate.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (detail && typeof detail === 'object' && detail.message) return detail.message;
  return fallback;
}

function isNotFound(error: unknown): boolean {
  return (error as { response?: { status?: number } }).response?.status === 404;
}

export const useRouteStore = create<RouteState>((set) => ({
  routes: [],
  selectedRoute: null,
  total: 0,
  page: 1,
  limit: 20,
  isLoading: false,
  isSaving: false,
  error: null,
  selectedRouteNotFound: false,
  clearError: () => set({ error: null }),

  fetchRoutes: async (query) => {
    set({ isLoading: true, error: null });
    try {
      const result = await listRoutes(query);
      set({ ...result, isLoading: false });
      return result;
    } catch (error) {
      if (isNotFound(error)) {
        const emptyResult: RoutePagination = { routes: [], total: 0, page: query.page ?? 1, limit: query.limit ?? 20 };
        set({ ...emptyResult, isLoading: false, error: null });
        return emptyResult;
      }
      set({ isLoading: false, error: errorMessage(error, 'Failed to load routes') });
      return null;
    }
  },

  fetchRoute: async (organizationId, routeId) => {
    set({ isLoading: true, error: null, selectedRouteNotFound: false });
    try {
      const route = await getRoute(organizationId, routeId);
      set({ selectedRoute: route, isLoading: false, selectedRouteNotFound: false });
      return route;
    } catch (error) {
      if (isNotFound(error)) {
        set({ selectedRoute: null, isLoading: false, error: null, selectedRouteNotFound: true });
        return null;
      }
      set({ isLoading: false, error: errorMessage(error, 'Failed to load route') });
      return null;
    }
  },

  createRoute: async (organizationId, input) => {
    set({ isSaving: true, error: null });
    try {
      const route = await createRoute(organizationId, input);
      set((state) => ({ routes: [route, ...state.routes], total: state.total + 1, isSaving: false }));
      return route;
    } catch (error) {
      set({ isSaving: false, error: errorMessage(error, 'Failed to create route') });
      return null;
    }
  },

  updateRoute: async (organizationId, routeId, input) => {
    set({ isSaving: true, error: null });
    try {
      const route = await updateRoute(organizationId, routeId, input);
      set((state) => ({
        routes: state.routes.map((row) => row.id === routeId ? route : row),
        selectedRoute: state.selectedRoute?.id === routeId ? route : state.selectedRoute,
        isSaving: false,
      }));
      return route;
    } catch (error) {
      set({ isSaving: false, error: errorMessage(error, 'Failed to update route') });
      return null;
    }
  },

  archiveRoute: async (organizationId, routeId) => {
    set({ isSaving: true, error: null });
    try {
      await archiveRoute(organizationId, routeId);
      set((state) => ({
        routes: state.routes.filter((row) => row.id !== routeId),
        total: Math.max(0, state.total - 1),
        selectedRoute: state.selectedRoute?.id === routeId ? null : state.selectedRoute,
        isSaving: false,
      }));
      return true;
    } catch (error) {
      set({ isSaving: false, error: errorMessage(error, 'Failed to archive route') });
      return false;
    }
  },

  assignTruck: async (organizationId, routeId, truckId) => {
    set({ isSaving: true, error: null });
    try {
      const route = await assignTruckToRoute(organizationId, routeId, truckId);
      set((state) => ({
        routes: state.routes.map((row) => row.id === routeId ? route : row),
        selectedRoute: state.selectedRoute?.id === routeId ? route : state.selectedRoute,
        isSaving: false,
      }));
      return route;
    } catch (error) {
      set({ isSaving: false, error: errorMessage(error, 'Failed to assign truck') });
      return null;
    }
  },

  unassignTruck: async (organizationId, routeId) => {
    set({ isSaving: true, error: null });
    try {
      const route = await unassignTruckFromRoute(organizationId, routeId);
      set((state) => ({
        routes: state.routes.map((row) => row.id === routeId ? route : row),
        selectedRoute: state.selectedRoute?.id === routeId ? route : state.selectedRoute,
        isSaving: false,
      }));
      return route;
    } catch (error) {
      set({ isSaving: false, error: errorMessage(error, 'Failed to unassign truck') });
      return null;
    }
  },
}));
